import React, { createContext, PropsWithChildren, useCallback, useRef, useState } from 'react';
import { useAppSelector } from 'hooks';
import { getNewSessionId } from 'utils/threatmetrix';

export type CashierTab = 'deposit' | 'withdraw' | 'history';
export type PoolType = 'digital' | 'inperson';
export type PaymentMethod = 'credit_card' | 'crypto' | 'paypal' | 'cage' | 'p2p';
export type CashierStep =
  | 'select'
  | 'amount'
  | 'method'
  | 'mfa'
  | 'processing'
  | 'success'
  | 'error';

type CashierContextType = {
  isOpen: boolean;
  openCashier: (tab?: CashierTab, prefillAmount?: number) => void;
  closeCashier: () => void;
  activeTab: CashierTab;
  setActiveTab: (tab: CashierTab) => void;
  selectedPool: PoolType | null;
  setSelectedPool: (pool: PoolType) => void;
  selectedMethod: PaymentMethod | null;
  setSelectedMethod: (method: PaymentMethod) => void;
  amount: number;
  setAmount: (amount: number) => void;
  step: CashierStep;
  setStep: (step: CashierStep) => void;
  prefillAmount: number;
  reset: () => void;
  // Kick off (or dedupe) a TMX device-profiling event for the given page index
  // (TMXPageData.*). Fire-and-forget from effects — the resolved sessionId is
  // cached for the subsequent transaction. Dedupes on page index, so repeated
  // calls for the same active tab/method (incl. StrictMode double-invoke) are
  // no-ops until the cache is cleared on open/close.
  profileForPage: (pageIndex: number) => void;
  // Submit-side read: awaits any in-flight profiling, then returns the cached
  // sessionId. Resolves to a real sid, the 'bypassed' sentinel, or null
  // (misconfig / missing playerGUID / nothing profiled yet). Never throws — the
  // value is passed straight to runRisk, which is the sole authority on bypass.
  awaitCashierSession: () => Promise<string | null>;
  // True when cashier was opened from the account menu — drives the back arrow's
  // "return to menu" behavior in CashierPanel.
  openedFromMenu: boolean;
  setOpenedFromMenu: (next: boolean) => void;
};

export const CashierContext = createContext<CashierContextType>({
  isOpen: false,
  openCashier: () => {},
  closeCashier: () => {},
  activeTab: 'deposit',
  setActiveTab: () => {},
  selectedPool: null,
  setSelectedPool: () => {},
  selectedMethod: null,
  setSelectedMethod: () => {},
  amount: 0,
  setAmount: () => {},
  step: 'select',
  setStep: () => {},
  prefillAmount: 0,
  reset: () => {},
  profileForPage: () => {},
  awaitCashierSession: async () => null,
  openedFromMenu: false,
  setOpenedFromMenu: () => {}
});

export const CashierProvider = ({ children }: PropsWithChildren) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<CashierTab>('deposit');
  const [selectedPool, setSelectedPool] = useState<PoolType | null>(null);
  const [selectedMethod, setSelectedMethod] = useState<PaymentMethod | null>(null);
  const [amount, setAmount] = useState(0);
  const [step, setStep] = useState<CashierStep>('select');
  const [prefillAmount, setPrefillAmount] = useState(0);
  const [openedFromMenu, setOpenedFromMenu] = useState(false);

  const playerGUID = useAppSelector((state) => state.user.playerGUID);
  // Cashier is unavailable during maintenance (Unity parity). Guard the single
  // open entry point so every caller (header, account menu, buy-in Add Funds)
  // is blocked without touching each call site.
  const isMaintenance = useAppSelector(
    (state) => (state as any).maintenanceSchedule?.isMaintenance
  );

  // TMX profiling cache. Refs (not state) — these are submit-time coordination
  // machinery, not render inputs.
  const sessionIdRef = useRef<string | null>(null); // last RESOLVED sid
  const inFlightRef = useRef<Promise<string | null> | null>(null); // current profile
  const profiledIndexRef = useRef<number | null>(null); // last page index kicked off

  const clearProfileCache = useCallback(() => {
    sessionIdRef.current = null;
    inFlightRef.current = null;
    profiledIndexRef.current = null;
  }, []);

  const profileForPage = useCallback(
    (pageIndex: number) => {
      // No playerGUID yet → skip. The activation effect lists playerGUID as a
      // dep, so this re-fires once it lands. getNewSessionId would no-op anyway.
      if (!playerGUID) return;

      // Dedupe: a profile for this exact index is already in-flight or resolved.
      // Covers StrictMode double-invoke and crypto↔cage (same Withdrawal index).
      // clearProfileCache() on open/close resets this so a re-open re-profiles.
      if (profiledIndexRef.current === pageIndex && inFlightRef.current) return;
      if (profiledIndexRef.current === pageIndex && sessionIdRef.current !== null) return;

      profiledIndexRef.current = pageIndex;
      sessionIdRef.current = null; // invalidate any stale sid while the new one resolves

      const profile = getNewSessionId(pageIndex, playerGUID)
        .then((sid) => {
          // Commit only if a newer profileForPage hasn't superseded us
          // (e.g. fast Withdraw→p2p) — the last-kicked profile wins.
          if (inFlightRef.current === profile) {
            sessionIdRef.current = sid; // string | 'bypassed' | null
            inFlightRef.current = null;
          }
          return sid;
        })
        .catch(() => {
          if (inFlightRef.current === profile) {
            sessionIdRef.current = null;
            inFlightRef.current = null;
          }
          return null;
        });

      inFlightRef.current = profile;
    },
    [playerGUID]
  );

  const awaitCashierSession = useCallback(async (): Promise<string | null> => {
    // Await any in-flight profile so submit never fires with a half-baked
    // session; otherwise return whatever is cached (sid | 'bypassed' | null).
    if (inFlightRef.current) return inFlightRef.current;
    return sessionIdRef.current;
  }, []);

  const reset = useCallback(() => {
    setSelectedPool(null);
    setSelectedMethod(null);
    setAmount(0);
    setStep('select');
  }, []);

  const openCashier = useCallback(
    (tab: CashierTab = 'deposit', prefill = 0) => {
      if (isMaintenance) return; // blocked during maintenance
      clearProfileCache(); // re-open re-profiles even if it lands on the same tab
      reset();
      setActiveTab(tab);
      setPrefillAmount(prefill);
      if (prefill > 0) setAmount(prefill);
      setIsOpen(true);
    },
    [reset, clearProfileCache, isMaintenance]
  );

  const closeCashier = useCallback(() => {
    clearProfileCache();
    setIsOpen(false);
    setOpenedFromMenu(false);
  }, [clearProfileCache]);

  return (
    <CashierContext.Provider
      value={{
        isOpen,
        openCashier,
        closeCashier,
        activeTab,
        setActiveTab,
        selectedPool,
        setSelectedPool,
        selectedMethod,
        setSelectedMethod,
        amount,
        setAmount,
        step,
        setStep,
        prefillAmount,
        reset,
        profileForPage,
        awaitCashierSession,
        openedFromMenu,
        setOpenedFromMenu
      }}>
      {children}
    </CashierContext.Provider>
  );
};
